// Nation look-ups shared by several widgets: colours and names by ISO3, the leader's state,
// and emblems. Everything resolves through the services API; failures fall back to neutral
// chrome, never to invented data.
//
// Emblems: a playable leader's laurelled initial appears only while that person holds the
// state's top office at the app date (CountryReport.leaders). Otherwise, and whenever the
// subject is a state rather than a person, the state emblem is drawn (flag or ISO3).

import { computed, type ReadonlySignal } from '@preact/signals';
import type { LeaderId, Perspective } from '../core/schema.ts';
import type { Services, WorldService } from '../services/api.ts';
import type { CountryReport } from '../world/api.ts';
import { actorInfoOf, flagOf } from './extensions.ts';
import type { IconName } from './icons.tsx';

export interface NationLook {
  iso3: string;
  name: string;
  colors: { primary: string; secondary: string };
}

const NEUTRAL = { primary: '#3b4b58', secondary: '#e6c77a' };

// Roles that make someone the face of a state, most senior first.
const TOP_ROLES = ['paramount_leader', 'head_of_state'];

// One computed per (world, state): the many look-ups of a frame cost one world query, and
// every reader subscribes to what the service read (date, leader), not only the first one.
// A per-frame cache would hide those reads from later readers, leaving them stale.
const reports = new WeakMap<WorldService, Map<string, ReadonlySignal<CountryReport | null>>>();

/** Country report, or null if the world service cannot answer for this id. */
export function reportOf(world: WorldService, iso3: string): CountryReport | null {
  let byIso = reports.get(world);
  if (!byIso) {
    byIso = new Map();
    reports.set(world, byIso);
  }

  let report = byIso.get(iso3);
  if (!report) {
    report = computed(() => {
      try {
        return world.country(iso3);
      } catch {
        return null;
      }
    });
    byIso.set(iso3, report);
  }
  return report.value;
}

export function lookOf(world: WorldService, iso3: string): NationLook {
  const r = reportOf(world, iso3);
  if (!r) {
    return { iso3, name: iso3, colors: NEUTRAL };
  }
  return { iso3, name: r.country.name, colors: r.country.colors };
}

/** Look of a snapshot actor index (countries and non-state actors). */
export function actorLook(world: WorldService, index: number): NationLook | null {
  const a = world.actor(index);
  if (!a) {
    return null;
  }
  return { iso3: a.id, name: a.name, colors: a.colors };
}

/** ISO3 of the leader's state, or null before a leader is chosen. */
export function selfIso(services: Services): string | null {
  const id = services.state.leader.value;
  if (!id) {
    return null;
  }
  return perspectiveOf(services.world, id)?.iso3 ?? null;
}

export function perspectiveOf(world: WorldService, id: LeaderId): Perspective | null {
  return world.perspectives().find((p) => p.id === id) ?? null;
}

// ─── Who holds office at the app date ────────────────────────────────────────────────────

export type Officeholder = CountryReport['leaders'][number];

/** The state's top officeholder at the app date (paramount leader, else head of state). */
export function topLeader(world: WorldService, iso3: string): Officeholder | null {
  const leaders = reportOf(world, iso3)?.leaders ?? [];
  for (const role of TOP_ROLES) {
    const hit = leaders.find((l) => l.role === role);
    if (hit) {
      return hit;
    }
  }
  return leaders[0] ?? null;
}

/** True when the perspective's leader holds a top office of their state at the app date. */
export function inOffice(world: WorldService, p: Perspective): boolean {
  const leaders = reportOf(world, p.iso3)?.leaders ?? [];
  return leaders.some((l) => l.name === p.name && TOP_ROLES.includes(l.role));
}

// ─── Emblems ─────────────────────────────────────────────────────────────────────────────

/** Medallion props for a nation: pass straight to <Medallion {...emblem} />. */
export interface Emblem {
  colors: { primary: string; secondary: string };
  mark: string;
  laurel?: 'laurel';
  flag?: string;
  glyph?: IconName;
}

/**
 * Emblem of a state. As `'person'`, a playable state shows its leader's laurelled initial
 * while that leader is in office at the app date; as `'state'` it always shows the state.
 */
export function emblemOf(world: WorldService, iso3: string, subject: 'person' | 'state'): Emblem {
  const look = lookOf(world, iso3);
  const p = world.perspectives().find((x) => x.iso3 === iso3);
  if (subject === 'person' && p && inOffice(world, p)) {
    return { colors: look.colors, mark: p.id.charAt(0).toUpperCase(), laurel: 'laurel' };
  }

  const flag = flagOf(world, iso3);
  return { colors: look.colors, mark: iso3, flag: flag ?? undefined };
}

/** Emblem of a perspective's leader, drawn from the perspective itself (start screen, switcher). */
export function leaderEmblem(world: WorldService, p: Perspective): Emblem {
  return { colors: lookOf(world, p.iso3).colors, mark: p.id.charAt(0).toUpperCase(), laurel: 'laurel' };
}

// ─── Any actor (states and non-state parties) ────────────────────────────────────────────

/** A glyph stands in for the flag and code that only states have. */
const GLYPH_OF_KIND: Record<string, IconName> = { nonstate: 'faction', bloc: 'alliance' };

/** Display name of an actor by id: a state's report name, else the actor table's, else the id. */
export function actorName(world: WorldService, id: string): string {
  return actorInfoOf(world, id)?.name ?? lookOf(world, id).name;
}

/** Kind of an actor by id ('country' for a state), or null when the service cannot say. */
export function actorKind(world: WorldService, id: string): string | null {
  return actorInfoOf(world, id)?.kind ?? null;
}

/** Emblem of any actor: a state's flag or code; a glyph by kind for a non-state party or a bloc. */
export function actorEmblem(world: WorldService, id: string): Emblem {
  const info = actorInfoOf(world, id);
  const glyph = info ? GLYPH_OF_KIND[info.kind] : undefined;
  if (!info || !glyph) {
    return emblemOf(world, id, 'state');
  }
  return { colors: info.colors, mark: '', glyph };
}
